import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { makeStyles, useTheme } from "@material-ui/core/styles"
import Card from "@material-ui/core/Card"
import CardHeader from "@material-ui/core/CardHeader"
import CardMedia from "@material-ui/core/CardMedia"
import CardContent from "@material-ui/core/CardContent"
import CardActions from "@material-ui/core/CardActions"
import Typography from "@material-ui/core/Typography"
import Button from "@material-ui/core/Button"
import Menu from "@material-ui/core/Menu"
import MenuItem from "@material-ui/core/MenuItem"
import { Divider, Grid, IconButton, Box } from "@material-ui/core"
import { PaginationDownSVG, PaginationUpSVG, ZoomInSVG, ZoomOutSVG } from "../icons"
import { Skeleton } from "@material-ui/lab"
import { useHistory } from "react-router-dom"
import ViewerPagination from "./ViewerPagination"
import ViewerDownloadFile from "./ViewerDownloadFile"
import ViewerHighlight from "./ViewerHighlight"
import AnnotationSVG from "../icons/AnnotationSVG"
import ToggleButton from "@material-ui/lab/ToggleButton"
import { searchHighlightFilter } from "../../actions/highlightActions"
import ViewerDocumentNavigation from "./ViewerDocumentNavigation"
import NativeViewSwitch from "./NativeViewSwitch"
import { RotateLeftImage, RotateRightImage, zoomInImage, zoomOutImage } from "../../actions/viewerActions"
import RotateLeftSVG from "../icons/RotateLeftSVG"
import RotateRightSVG from "../icons/RotateRightSVG"

const useStyles = makeStyles((theme) => ({
	root: {
		...theme.typography.body1,
		backgroundColor: theme.palette.common.white,
		border: `solid 1px ${theme.palette.grey.D400}`,
		borderRadius: "5px",
		marginBottom: "20px",
		display: "flex",
		"& .MuiToggleButton-root.Mui-selected": {
			background: "rgba(52, 118, 228, 0.12)",
			borderRadius: 0,
		},
		"& .MuiToggleButton-root": {
			borderRight: `solid 1px ${theme.palette.grey.D400}`,
		},
		overflow: "auto",
	},
	highlight: { flex: 3, padding: "16px 23px", borderRight: `solid 1px ${theme.palette.grey.D400}` },
	pagination: {
		flex: 2,
		display: "flex",
		padding: "16px 23px",
		borderRight: `solid 1px ${theme.palette.grey.D400}`,
		justifyContent: "center",
		alignItems: "center",
	},
	download: { flex: 1, padding: "16px 23px", borderRight: `solid 1px ${theme.palette.grey.D400}` },
}))

const ViewerHeader = (props) => {
	const classes = useStyles()
	const dispatch = useDispatch()
	const history = useHistory()
	const theme = useTheme()
	const highlightFilter = useSelector((state) => state.highlightFilter.search)
	const { documentDetails, documentLoading } = useSelector((state) => state.documentModal)
	const { imageZoomScale, imageRotatePosition } = useSelector((state) => state.viewerData)

	function shouldShowHighlightComponent(type, isNativeViewOn) {
		if (type === "audio" || type === "video") {
			return false
		} else if (type === "img") {
			return !isNativeViewOn
		} else {
			return true
		}
	}

	return (
		<Box className={classes.root}>
			{props?.loading ? (
				<Skeleton variant="rect" width="100%" style={{ margin: "15px", padding: "20px" }}></Skeleton>
			) : (
				<>
					{["img"].includes(documentDetails?.metadata?.source) && <NativeViewSwitch />}
					{shouldShowHighlightComponent(documentDetails?.metadata?.source, highlightFilter?.nativeView) && (
						<ViewerHighlight {...props} />
					)}

					<ViewerDocumentNavigation {...props} />
					{!["img", "audio", "video"].includes(documentDetails?.metadata?.source) && (
						<>
							<ViewerPagination {...props} />
							<ToggleButton
								value="annotation"
								selected={highlightFilter?.annotation}
								onChange={() => {
									const filterData = { ...highlightFilter }
									if (!highlightFilter["annotation"]) {
										const saveHighlightState = {
											emails: filterData["emails"],
											specificWord: filterData["specificWord"],
											dates: filterData["dates"],
											concepts: filterData["concepts"],
										}
										filterData["saveHighlightState"] = saveHighlightState
										filterData["emails"] = false
										filterData["specificWord"] = false
										filterData["dates"] = false
										filterData["concepts"] = false
										filterData["annotation"] = true
										dispatch(searchHighlightFilter(filterData))
									} else {
										filterData["emails"] = filterData["saveHighlightState"]?.emails
										filterData["specificWord"] = filterData["saveHighlightState"]?.specificWord
										filterData["dates"] = filterData["saveHighlightState"]?.dates
										filterData["concepts"] = filterData["saveHighlightState"]?.concepts
										filterData["annotation"] = false
										filterData["saveHighlightState"] = {}
										dispatch(searchHighlightFilter(filterData))
									}
								}}
							>
								<AnnotationSVG />
							</ToggleButton>
						</>
					)}
					{["img"].includes(documentDetails?.metadata?.source) && highlightFilter?.nativeView && (
						<>
							<Box className={classes.pagination}>
								<Box
									onClick={() => {
										if (Math.round(imageZoomScale * 100) > 20) {
											dispatch(zoomOutImage(parseFloat(imageZoomScale - 0.1)))
										}
									}}
									style={{
										padding: "0px 10px",
										display: "flex",
										cursor: "pointer",
										opacity: Math.round(imageZoomScale * 100) > 20 ? 1 : 0.5,
									}}
								>
									<ZoomOutSVG />
								</Box>
								<Box>{`${Math.round(imageZoomScale * 100)}%`}</Box>

								<Box
									onClick={() => {
										if (Math.round(imageZoomScale * 100) < 200) {
											dispatch(zoomInImage(parseFloat(imageZoomScale + 0.1)))
										}
									}}
									style={{
										display: "flex",
										cursor: "pointer",
										padding: "0px 10px",
										opacity: Math.round(imageZoomScale * 100) < 200 ? 1 : 0.5,
									}}
								>
									<ZoomInSVG />
								</Box>
							</Box>
							<Box className={classes.pagination}>
								<Box
									onClick={() => {
										dispatch(zoomInImage(parseFloat(1.0)))
										dispatch(RotateLeftImage(parseInt(imageRotatePosition - 90)))
									}}
									style={{
										padding: "0px 10px",
										display: "flex",
										cursor: "pointer",
									}}
								>
									<RotateLeftSVG />
								</Box>
								<Box
									onClick={() => {
										dispatch(zoomInImage(parseFloat(1.0)))
										dispatch(RotateRightImage(parseInt(imageRotatePosition + 90)))
									}}
									style={{
										display: "flex",
										cursor: "pointer",
										padding: "0px 10px",
									}}
								>
									<RotateRightSVG />
								</Box>
							</Box>
						</>
					)}
					<ViewerDownloadFile {...props} />
				</>
			)}
		</Box>
	)
}

export default ViewerHeader
